import { useState } from "react";
import axios from "axios";
import { confirmAssignmentChange } from "../../utils/confirmAssignmentChange";

export async function confirmDriverProgressChange(request) {
  try {
    await request({});
  } catch (error) {
    const detail = error.response?.data?.detail;
    if (detail?.code !== "driver_progress_confirmation_required") throw error;
    if (!await confirmAssignmentChange(detail.message)) return false;
    await request({
      confirm_driver_status: detail.driver_status,
      confirm_assignment_version: detail.assignment_version,
    });
  }
  return true;
}

const initialDriverForm = {
  driver_id: "",
  vehicle_brand: "TOYOTA",
  vehicle_type: "alphard",
  plate_number: "",
  driver_name: "",
  driver_phone: "",
  dispatch_price: "",
  fleet_dispatch_price: "",
  dispatch_price_reason: "",
  is_favorite: false,
};

const initialDriverAccountForm = {
  name: "",
  phone: "",
  password: "",
  can_view_revenue_stats: false,
  include_in_internal_dispatch_overview: false,
  can_request_leave: false,
  instruction_region: "other",
  instruction_header_id: "",
  is_vip: false,
};

const initialOrderPoolForm = {
  dispatch_price: "",
};

const initialDriverComplaintForm = {
  issue_types: [],
  deduction_points: "",
  note: "",
};

const initialDriverNoticeForm = {
  title: "",
  body: "",
  source_language: "zh_cn",
  translations: {
    zh_cn: { title: "", body: "" },
    zh_tw: { title: "", body: "" },
    ja: { title: "", body: "" },
    en: { title: "", body: "" },
  },
  is_active: true,
};

const isCompletedOrderForDispatchPriceEdit = (order) => {
  const orderStatus = String(order?.status || "").trim().toLowerCase();
  const driverStatus = String(order?.driver_order_status || "").trim().toLowerCase();
  return orderStatus === "completed" || orderStatus === "已完成" || driverStatus === "completed" || driverStatus === "已完成";
};

const getCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getDownloadFilename = (response, fallbackName) => {
  const disposition = response.headers?.["content-disposition"] || "";
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch) {
    return decodeURIComponent(encodedMatch[1]);
  }
  return fallbackName;
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getDriverComplaintOrderId = (order) => order?.id || order?.order_id || order?.orderId || order?.order_no || order?.confirmation_code || order?._id || "";

export default function useAdminDrivers({ apiBaseUrl, fetchOrders, getErrorMessage, notifyAdminApp, refreshStats }) {
  const [showDriverAssignModal, setShowDriverAssignModal] = useState(false);
  const [selectedOrderForDriver, setSelectedOrderForDriver] = useState(null);
  const [savedDrivers, setSavedDrivers] = useState([]);
  const [driverForm, setDriverForm] = useState(initialDriverForm);

  const [driverAccounts, setDriverAccounts] = useState([]);
  const [driverAccountsLoading, setDriverAccountsLoading] = useState(false);
  const [driverAccountsError, setDriverAccountsError] = useState("");
  const [showDriverAccountForm, setShowDriverAccountForm] = useState(false);
  const [editingDriverAccount, setEditingDriverAccount] = useState(null);
  const [driverAccountForm, setDriverAccountForm] = useState(initialDriverAccountForm);

  const [showDriverSchedule, setShowDriverSchedule] = useState(false);
  const [selectedDriverForSchedule, setSelectedDriverForSchedule] = useState(null);
  const [driverScheduleOrders, setDriverScheduleOrders] = useState([]);
  const [driverScheduleLoading, setDriverScheduleLoading] = useState(false);
  const [hideCompletedDriverOrders, setHideCompletedDriverOrders] = useState(true);
  const [driverExtraSchedules, setDriverExtraSchedules] = useState([]);
  const [driverMonthlyStatsMonth, setDriverMonthlyStatsMonth] = useState(getCurrentMonthValue());
  const [driverMonthlyStatsDispatcherId, setDriverMonthlyStatsDispatcherId] = useState("");
  const [driverMonthlyStatsDriverId, setDriverMonthlyStatsDriverId] = useState("");
  const [driverMonthlyStats, setDriverMonthlyStats] = useState(null);
  const [driverMonthlyStatsLoading, setDriverMonthlyStatsLoading] = useState(false);
  const [selectedDriverOnboarding, setSelectedDriverOnboarding] = useState(null);
  const [driverOnboardingLoading, setDriverOnboardingLoading] = useState(false);
  const [driverTrainingConfig, setDriverTrainingConfig] = useState(null);
  const [showDriverTrainingConfig, setShowDriverTrainingConfig] = useState(false);
  const [driverNotices, setDriverNotices] = useState([]);
  const [showDriverNoticeModal, setShowDriverNoticeModal] = useState(false);
  const [driverNoticeForm, setDriverNoticeForm] = useState(initialDriverNoticeForm);
  const [editingDriverNotice, setEditingDriverNotice] = useState(null);
  const [driverNoticeTranslating, setDriverNoticeTranslating] = useState(false);
  const [showOrderPoolForm, setShowOrderPoolForm] = useState(false);
  const [selectedOrderForPool, setSelectedOrderForPool] = useState(null);
  const [orderPoolForm, setOrderPoolForm] = useState(initialOrderPoolForm);
  const [orderPoolSubmitting, setOrderPoolSubmitting] = useState(false);
  const [orderPoolError, setOrderPoolError] = useState("");
  const [showDriverComplaintForm, setShowDriverComplaintForm] = useState(false);
  const [selectedOrderForComplaint, setSelectedOrderForComplaint] = useState(null);
  const [driverComplaintOptions, setDriverComplaintOptions] = useState([]);
  const [driverComplaintForm, setDriverComplaintForm] = useState(initialDriverComplaintForm);
  const [driverComplaintSubmitting, setDriverComplaintSubmitting] = useState(false);
  const [driverComplaintOptionsLoading, setDriverComplaintOptionsLoading] = useState(false);
  const [driverComplaintError, setDriverComplaintError] = useState("");
  const showDriverNotice = (message) => {
    if (typeof notifyAdminApp === "function") {
      notifyAdminApp(message);
      return true;
    }
    return false;
  };
  const showDriverComplaintNotice = (message) => {
    if (!showDriverNotice(message)) {
      alert(message);
    }
  };

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const refreshOrdersAndStats = () => {
    fetchOrders?.();
    refreshStats?.();
  };

  const fetchSavedDrivers = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/saved-drivers`, getAuthConfig());
      const sorted = (response.data || []).sort((a, b) => {
        if (a.is_favorite && !b.is_favorite) return -1;
        if (!a.is_favorite && b.is_favorite) return 1;
        return 0;
      });
      setSavedDrivers(sorted);
    } catch (error) {
      console.error("获取司机列表失败:", error);
      setSavedDrivers([]);
    }
  };

  const handleSaveDriver = async () => {
    if (!driverForm.driver_name || !driverForm.plate_number) {
      alert("请填写司机姓名和车牌号");
      return;
    }

    try {
      await axios.post(`${apiBaseUrl}/admin/saved-drivers`, driverForm, getAuthConfig());
      alert("司机信息已保存");
      fetchSavedDrivers();
    } catch (error) {
      alert("保存失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm("确定要删除此司机信息吗？")) return;
    try {
      await axios.delete(`${apiBaseUrl}/admin/saved-drivers/${driverId}`, getAuthConfig());
      fetchSavedDrivers();
    } catch (error) {
      alert("删除失败");
    }
  };

  const handleToggleFavoriteDriver = async (driverId, currentFavorite) => {
    try {
      await axios.put(`${apiBaseUrl}/admin/saved-drivers/${driverId}`, {
        is_favorite: !currentFavorite
      }, getAuthConfig());
      fetchSavedDrivers();
    } catch (error) {
      alert("操作失败");
    }
  };

  const fetchDriverAccounts = async () => {
    setDriverAccountsLoading(true);
    setDriverAccountsError("");
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/drivers`, getAuthConfig());
      setDriverAccounts(response.data || []);
    } catch (error) {
      console.error("获取司机账号失败:", error);
      setDriverAccountsError(getErrorMessage(error, "获取司机账号失败"));
      setDriverAccounts((current) => current || []);
    } finally {
      setDriverAccountsLoading(false);
    }
  };

  const buildDriverMonthlyStatsParams = (
    month = driverMonthlyStatsMonth,
    dispatcherId = driverMonthlyStatsDispatcherId,
    driverId = driverMonthlyStatsDriverId,
  ) => ({
    month,
    ...(dispatcherId ? { dispatcher_id: dispatcherId } : {}),
    ...(driverId ? { driver_id: driverId } : {}),
  });

  const fetchDriverMonthlyStats = async (
    month = driverMonthlyStatsMonth,
    dispatcherId = driverMonthlyStatsDispatcherId,
    driverId = driverMonthlyStatsDriverId,
  ) => {
    setDriverMonthlyStatsLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/drivers/monthly-stats`, {
        ...getAuthConfig(),
        params: buildDriverMonthlyStatsParams(month, dispatcherId, driverId),
      });
      setDriverMonthlyStats(response.data || null);
    } catch (error) {
      console.error("获取司机月度统计失败:", error);
      setDriverMonthlyStats(null);
      alert(getErrorMessage(error, "获取司机月度统计失败"));
    } finally {
      setDriverMonthlyStatsLoading(false);
    }
  };

  const handleDriverMonthlyStatsMonthChange = (month) => {
    setDriverMonthlyStatsMonth(month);
    fetchDriverMonthlyStats(month, driverMonthlyStatsDispatcherId, driverMonthlyStatsDriverId);
  };

  const handleDriverMonthlyStatsDispatcherChange = (dispatcherId) => {
    setDriverMonthlyStatsDispatcherId(dispatcherId);
    setDriverMonthlyStatsDriverId("");
    fetchDriverMonthlyStats(driverMonthlyStatsMonth, dispatcherId, "");
  };

  const handleDriverMonthlyStatsDriverChange = (driverId) => {
    setDriverMonthlyStatsDriverId(driverId);
    fetchDriverMonthlyStats(driverMonthlyStatsMonth, driverMonthlyStatsDispatcherId, driverId);
  };

  const handleExportDriverMonthlyStats = async (orderIds = []) => {
    const selectedOrderIds = Array.from(new Set(orderIds || [])).filter(Boolean);
    setDriverMonthlyStatsLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/drivers/monthly-stats/export`, {
        ...getAuthConfig(),
        params: {
          ...buildDriverMonthlyStatsParams(),
          ...(selectedOrderIds.length ? { order_ids: selectedOrderIds.join(",") } : {}),
        },
        responseType: "blob",
      });
      const filename = getDownloadFilename(response, `driver_monthly_stats_${driverMonthlyStatsMonth}.xlsx`);
      triggerBlobDownload(response.data, filename);
    } catch (error) {
      console.error("导出司机月度统计失败:", error);
      alert(getErrorMessage(error, "导出司机月度统计失败"));
    } finally {
      setDriverMonthlyStatsLoading(false);
    }
  };

  const handleUpdateDriverMonthlyDispatchPrice = async (orderId, value, reason = "") => {
    const dispatchPrice = value === "" ? null : Number(value);
    if (dispatchPrice !== null && !Number.isInteger(dispatchPrice)) {
      throw new Error("派单价格必须是整数日元");
    }
    const cleanedReason = String(reason || "").trim();
    if (dispatchPrice !== null && dispatchPrice < 0 && !cleanedReason) {
      throw new Error("派单价格为负数时必须填写备注/理由");
    }
    try {
      const response = await axios.put(
        `${apiBaseUrl}/admin/orders/${orderId}/dispatch-price`,
        { dispatch_price: dispatchPrice, dispatch_price_reason: cleanedReason },
        getAuthConfig(),
      );
      await Promise.all([
        fetchDriverMonthlyStats(),
        Promise.resolve(fetchOrders?.()),
      ]);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, "保存派单价格失败"));
    }
  };

  const handleSubmitDriverMonthlySettlements = async (orderIds) => {
    const uniqueOrderIds = Array.from(new Set(orderIds || [])).filter(Boolean);
    if (uniqueOrderIds.length === 0) return null;
    setDriverMonthlyStatsLoading(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/settlements/batch`,
        { order_ids: uniqueOrderIds },
        getAuthConfig(),
      );
      await Promise.all([
        fetchDriverMonthlyStats(),
        Promise.resolve(fetchOrders?.()),
      ]);
      const result = response.data || {};
      const message = [
        `已递交结算：${result.submitted_count || 0}单`,
        result.already_submitted_count ? `已递交过：${result.already_submitted_count}单` : "",
        result.failed_count ? `未递交：${result.failed_count}单` : "",
      ].filter(Boolean).join("\n");
      window.alert(message);
      return result;
    } catch (error) {
      throw new Error(getErrorMessage(error, "批量递交结算失败"));
    } finally {
      setDriverMonthlyStatsLoading(false);
    }
  };

  const handleCreateDriverAccount = async () => {
    if (!driverAccountForm.name || !driverAccountForm.phone || !driverAccountForm.password) {
      alert("请填写完整信息");
      return;
    }
    if (driverAccountForm.password.length < 6) {
      alert("密码至少需要6个字符");
      return;
    }
    try {
      await axios.post(`${apiBaseUrl}/admin/drivers`, {
        name: driverAccountForm.name,
        phone: driverAccountForm.phone,
        password: driverAccountForm.password,
        can_view_revenue_stats: Boolean(driverAccountForm.can_view_revenue_stats),
        include_in_internal_dispatch_overview: Boolean(driverAccountForm.include_in_internal_dispatch_overview),
        can_request_leave: Boolean(driverAccountForm.can_request_leave),
        instruction_region: driverAccountForm.instruction_region || "other",
        instruction_header_id: driverAccountForm.instruction_header_id || "",
        is_vip: Boolean(driverAccountForm.is_vip),
      }, getAuthConfig());
      alert("司机账号创建成功");
      setShowDriverAccountForm(false);
      setDriverAccountForm(initialDriverAccountForm);
      fetchDriverAccounts();
    } catch (error) {
      alert("创建失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateDriverAccount = async () => {
    if (!editingDriverAccount) return;
    try {
      await axios.put(`${apiBaseUrl}/admin/drivers/${editingDriverAccount.id}`, {
        name: driverAccountForm.name,
        phone: driverAccountForm.phone,
        can_view_revenue_stats: Boolean(driverAccountForm.can_view_revenue_stats),
        include_in_internal_dispatch_overview: Boolean(driverAccountForm.include_in_internal_dispatch_overview),
        can_request_leave: Boolean(driverAccountForm.can_request_leave),
        instruction_region: driverAccountForm.instruction_region || "other",
        instruction_header_id: driverAccountForm.instruction_header_id || "",
        is_vip: Boolean(driverAccountForm.is_vip),
      }, getAuthConfig());
      alert("司机信息更新成功");
      setEditingDriverAccount(null);
      setShowDriverAccountForm(false);
      setDriverAccountForm(initialDriverAccountForm);
      fetchDriverAccounts();
    } catch (error) {
      alert("更新失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetDriverPassword = async (driverId) => {
    const newPassword = window.prompt("请输入新密码（至少6个字符）:");
    if (!newPassword) return;
    if (newPassword.length < 6) {
      alert("密码至少需要6个字符");
      return;
    }
    try {
      await axios.post(`${apiBaseUrl}/admin/drivers/${driverId}/reset-password`, {
        new_password: newPassword
      }, getAuthConfig());
      alert("密码重置成功");
    } catch (error) {
      alert("重置失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleDriverActive = async (driverId) => {
    try {
      const response = await axios.put(`${apiBaseUrl}/admin/drivers/${driverId}/toggle-active`, {}, getAuthConfig());
      alert(response.data.message);
      fetchDriverAccounts();
    } catch (error) {
      alert("操作失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleDriverPinned = async (driver) => {
    const is_pinned = !Boolean(driver.is_pinned);
    await axios.put(`${apiBaseUrl}/admin/drivers/${driver.id}`, { is_pinned }, getAuthConfig());
    setDriverAccounts(current => current.map(item => item.id === driver.id ? { ...item, is_pinned } : item));
  };

  const handleToggleDriverRevenueStats = async (driver) => {
    try {
      await axios.put(`${apiBaseUrl}/admin/drivers/${driver.id}`, {
        can_view_revenue_stats: !Boolean(driver.can_view_revenue_stats),
      }, getAuthConfig());
      fetchDriverAccounts();
    } catch (error) {
      alert("操作失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleDriverInternalDispatchOverview = async (driver) => {
    try {
      await axios.put(`${apiBaseUrl}/admin/drivers/${driver.id}`, {
        include_in_internal_dispatch_overview: !Boolean(driver.include_in_internal_dispatch_overview),
      }, getAuthConfig());
      fetchDriverAccounts();
    } catch (error) {
      alert("操作失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteDriverAccount = async (driverId) => {
    if (!window.confirm("确定要删除此司机账号吗？该操作不可恢复。")) return;
    try {
      await axios.delete(`${apiBaseUrl}/admin/drivers/${driverId}`, getAuthConfig());
      alert("司机账号已删除");
      fetchDriverAccounts();
    } catch (error) {
      alert("删除失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchDriverOnboarding = async (driver) => {
    if (!driver?.id) return;
    setDriverOnboardingLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/drivers/${driver.id}/onboarding`, getAuthConfig());
      setSelectedDriverOnboarding({ driver, onboarding: response.data || {} });
    } catch (error) {
      alert(getErrorMessage(error, "获取司机入驻资料失败"));
    } finally {
      setDriverOnboardingLoading(false);
    }
  };

  const handleReviewDriverOnboarding = async (driverId, step, status) => {
    const note = window.prompt(status === "approved" ? "审核备注（可选）" : "退回原因:", "") || "";
    if (status === "rejected" && !note.trim()) {
      alert("退回原因不能为空");
      return;
    }
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/drivers/${driverId}/onboarding/review`, {
        step,
        status,
        note: note.trim(),
      }, getAuthConfig());
      setSelectedDriverOnboarding((current) => current ? {
        ...current,
        onboarding: response.data || {},
      } : current);
      fetchDriverAccounts();
    } catch (error) {
      alert(getErrorMessage(error, "审核失败"));
    }
  };

  const fetchDriverTrainingConfig = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/driver-training-config`, getAuthConfig());
      setDriverTrainingConfig(response.data || null);
      setShowDriverTrainingConfig(true);
    } catch (error) {
      alert(getErrorMessage(error, "获取乗務前研修配置失败"));
    }
  };

  const handleSaveDriverTrainingConfig = async (nextConfig = driverTrainingConfig) => {
    try {
      const response = await axios.put(`${apiBaseUrl}/admin/driver-training-config`, {
        lessons: nextConfig?.lessons || [],
        questions: nextConfig?.questions || [],
      }, getAuthConfig());
      setDriverTrainingConfig(response.data || null);
      alert("乗務前研修配置已保存");
    } catch (error) {
      alert(getErrorMessage(error, "保存乗務前研修配置失败"));
    }
  };

  const fetchDriverNotices = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/driver-notices`, getAuthConfig());
      setDriverNotices(response.data || []);
      setDriverNoticeForm(initialDriverNoticeForm);
      setEditingDriverNotice(null);
      setShowDriverNoticeModal(true);
    } catch (error) {
      alert(getErrorMessage(error, "获取司机お知らせ失败"));
    }
  };

  const handleEditDriverNotice = (notice) => {
    setEditingDriverNotice(notice);
    setDriverNoticeForm({
      title: notice?.title || "",
      body: notice?.body || "",
      source_language: notice?.source_language || "zh_cn",
      translations: {
        ...initialDriverNoticeForm.translations,
        ...(notice?.translations || {}),
      },
      is_active: notice?.is_active !== false,
    });
  };

  const handleTranslateDriverNotice = async () => {
    if (!driverNoticeForm.title.trim() || !driverNoticeForm.body.trim()) {
      alert("请先填写原文标题和内容");
      return;
    }
    setDriverNoticeTranslating(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/driver-notices/translate`, {
        title: driverNoticeForm.title,
        body: driverNoticeForm.body,
        source_language: driverNoticeForm.source_language || "zh_cn",
      }, getAuthConfig());
      setDriverNoticeForm({
        ...driverNoticeForm,
        source_language: response.data?.source_language || driverNoticeForm.source_language || "zh_cn",
        translations: {
          ...initialDriverNoticeForm.translations,
          ...(response.data?.translations || {}),
        },
      });
      alert("AI翻译已生成，请检查四个语言版本后发布");
    } catch (error) {
      alert(getErrorMessage(error, "AI翻译失败，请检查AI配置或稍后重试"));
    } finally {
      setDriverNoticeTranslating(false);
    }
  };

  const handleSaveDriverNotice = async () => {
    if (!driverNoticeForm.title.trim() || !driverNoticeForm.body.trim()) {
      alert("请填写标题和内容");
      return;
    }
    const payload = {
      title: driverNoticeForm.title,
      body: driverNoticeForm.body,
      source_language: driverNoticeForm.source_language || "zh_cn",
      translations: driverNoticeForm.translations || {},
      is_active: Boolean(driverNoticeForm.is_active),
    };
    try {
      if (editingDriverNotice?.id) {
        await axios.put(`${apiBaseUrl}/admin/driver-notices/${editingDriverNotice.id}`, payload, getAuthConfig());
      } else {
        await axios.post(`${apiBaseUrl}/admin/driver-notices`, payload, getAuthConfig());
      }
      const response = await axios.get(`${apiBaseUrl}/admin/driver-notices`, getAuthConfig());
      setDriverNotices(response.data || []);
      setDriverNoticeForm(initialDriverNoticeForm);
      setEditingDriverNotice(null);
      alert("お知らせ已发布");
    } catch (error) {
      alert(getErrorMessage(error, "保存お知らせ失败"));
    }
  };

  const handleMigrateDriverOrders = async () => {
    if (!window.confirm("确定要执行历史订单迁移吗？这将根据手机号关联已配车的订单到司机账号。")) return;
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/drivers/migrate-orders`, {}, getAuthConfig());
      alert(`迁移完成！\n已迁移: ${response.data.migrated} 单\n跳过: ${response.data.skipped} 单`);
      fetchOrders();
    } catch (error) {
      alert("迁移失败: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchDriverExtraSchedules = async (driverId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiBaseUrl}/admin/extra-schedule/driver/${driverId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriverExtraSchedules(response.data || []);
    } catch (error) {
      console.error("获取司机追加订单失败:", error);
      setDriverExtraSchedules([]);
    }
  };

  const fetchDriverSchedule = async (driver) => {
    setDriverScheduleLoading(true);
    setSelectedDriverForSchedule(driver);
    setShowDriverSchedule(true);

    try {
      const response = await axios.get(`${apiBaseUrl}/orders`);
      const driverOrders = (response.data || []).filter(order => {
        const assignedDriver = order.assigned_driver;
        if (!assignedDriver) return false;
        return assignedDriver.driver_account_id === driver.id ||
               assignedDriver.driver_phone === driver.phone;
      });

      driverOrders.sort((a, b) => {
        const dateA = a.departure_date || "9999-99-99";
        const dateB = b.departure_date || "9999-99-99";
        if (dateA !== dateB) {
          return dateA.localeCompare(dateB);
        }
        const timeA = a.pickup_time || "99:99";
        const timeB = b.pickup_time || "99:99";
        return timeA.localeCompare(timeB);
      });

      setDriverScheduleOrders(driverOrders);
      fetchDriverExtraSchedules(driver.id);
    } catch (error) {
      console.error("获取司机排班失败:", error);
      alert(getErrorMessage(error, "获取司机排班失败"));
    } finally {
      setDriverScheduleLoading(false);
    }
  };

  const handleCancelExtraSchedule = async (scheduleId) => {
    if (!window.confirm("确定要取消这个追加排班吗？")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${apiBaseUrl}/admin/extra-schedule/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedDriverForSchedule) {
        fetchDriverExtraSchedules(selectedDriverForSchedule.id);
      }
    } catch (error) {
      console.error("取消追加订单失败:", error);
      alert(getErrorMessage(error, "取消追加订单失败"));
    }
  };

  const closeDriverSchedule = () => {
    setShowDriverSchedule(false);
    setSelectedDriverForSchedule(null);
    setDriverScheduleOrders([]);
    setDriverExtraSchedules([]);
  };

  const handleAssignDriverToOrder = async (orderId, driverInfo) => {
    if (isCompletedOrderForDispatchPriceEdit(selectedOrderForDriver)) {
      const dispatchPrice = driverInfo.dispatch_price === "" ? null : Number(driverInfo.dispatch_price);
      if (dispatchPrice !== null && !Number.isInteger(dispatchPrice)) {
        alert("派单价格必须是整数日元");
        return;
      }
      const dispatchPriceReason = String(driverInfo.dispatch_price_reason || "").trim();
      if (dispatchPrice !== null && dispatchPrice < 0 && !dispatchPriceReason) {
        alert("派单价格为负数时必须填写备注/理由");
        return;
      }
      const oldDriverId = selectedOrderForDriver.assigned_driver_id || selectedOrderForDriver.assigned_driver?.driver_account_id || selectedOrderForDriver.assigned_driver?.driver_id || "";
      if (!driverInfo.driver_id && !selectedOrderForDriver.assigned_driver) {
        alert("请选择司机");
        return;
      }
      const fleetDispatchPrice = !driverInfo.fleet_id || !driverInfo.fleet_dispatch_price ? null : Number(driverInfo.fleet_dispatch_price);
      if (fleetDispatchPrice !== null && !Number.isInteger(fleetDispatchPrice)) {
        alert("派单价格必须是整数日元");
        return;
      }
      const driverChanged = Boolean(driverInfo.driver_id && driverInfo.driver_id !== oldDriverId);
      if (!window.confirm(driverChanged ? "确认补录此已完成订单的实际服务司机？订单仍保持已完成，不发送新派单通知。" : "订单已完成，是否确认继续修改派单价格？")) return;

      try {
        await axios.put(
          driverChanged ? `${apiBaseUrl}/orders/${orderId}/completed-driver` : `${apiBaseUrl}/admin/orders/${orderId}/dispatch-price`,
          driverChanged ? {...driverInfo, dispatch_price: dispatchPrice, fleet_dispatch_price: fleetDispatchPrice, dispatch_price_reason: dispatchPriceReason, is_temp_driver:false} : { dispatch_price: dispatchPrice, dispatch_price_reason: dispatchPriceReason },
          driverChanged ? {...getAuthConfig(), params:{confirm_assignment_version:selectedOrderForDriver.assignment_version || 0}} : getAuthConfig(),
        );
        alert(driverChanged ? "已完成订单的司机已保存" : "已完成订单的派单价格已修改");
        setShowDriverAssignModal(false);
        refreshOrdersAndStats();
      } catch (error) {
        alert(getErrorMessage(error, "修改派单价格失败"));
      }
      return;
    }

    const dispatchPrice = driverInfo.dispatch_price === "" ? null : Number(driverInfo.dispatch_price);
    const fleetDispatchPrice = !driverInfo.fleet_id || driverInfo.fleet_dispatch_price === ""
      ? null
      : Number(driverInfo.fleet_dispatch_price);
    if ([dispatchPrice, fleetDispatchPrice].some((price) => price !== null && !Number.isInteger(price))) {
      alert("派单价格必须是整数日元");
      return;
    }
    const dispatchPriceReason = String(driverInfo.dispatch_price_reason || "").trim();
    if ([dispatchPrice, fleetDispatchPrice].some((price) => price !== null && price < 0) && !dispatchPriceReason) {
      alert("派单价格为负数时必须填写备注/理由");
      return;
    }

    try {
      const isTempDriver = !driverInfo.driver_id;
      const changed = await confirmDriverProgressChange((params) => axios.put(`${apiBaseUrl}/orders/${orderId}/assign-driver`, {
        ...driverInfo,
        dispatch_price: dispatchPrice,
        fleet_dispatch_price: fleetDispatchPrice,
        dispatch_price_reason: dispatchPriceReason,
        is_temp_driver: isTempDriver
      }, { ...getAuthConfig(), params }));
      if (!changed) return;
      alert(isTempDriver ? "临时司机已分配（司机端不可见）" : "司机已分配");
      setShowDriverAssignModal(false);
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "分配失败，请刷新后重试"));
    }
  };

  const handleUnassignDriver = async (orderId) => {
    if (!await confirmAssignmentChange("确定要取消配车吗？\n司机端将看不到此订单。")) return;

    try {
      const changed = await confirmDriverProgressChange((params) => axios.delete(`${apiBaseUrl}/orders/${orderId}/unassign-driver`, { ...getAuthConfig(), params }));
      if (!changed) return;
      alert("配车已取消");
      setShowDriverAssignModal(false);
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "取消配车失败"));
    }
  };

  const handleNotifyAssignedDriver = async (orderId) => {
    if (!orderId) return;
    try {
      const response = await axios.post(`${apiBaseUrl}/orders/${orderId}/notify-driver`, {}, getAuthConfig());
      const notification = response.data?.notification || {};
      const status = notification.send_status || "unknown";
      const tokenCount = notification.token_count ?? notification.send_result?.token_count ?? 0;
      alert(`已手动通知司机\n推送状态: ${status}\nToken: ${tokenCount}`);
    } catch (error) {
      alert(getErrorMessage(error, "通知司机失败"));
    }
  };

  const handleResolveDriverIssue = async (orderId) => {
    if (!orderId) return;
    try {
      await axios.post(`${apiBaseUrl}/orders/${orderId}/driver-issue/resolve`, {}, getAuthConfig());
      alert("司机异常已标记处理");
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "标记司机异常失败"));
    }
  };

  const handlePutOrderIntoPool = (order) => {
    if (!order?.id) return;
    setSelectedOrderForPool(order);
    setOrderPoolForm(initialOrderPoolForm);
    setOrderPoolError("");
    setShowOrderPoolForm(true);
  };

  const closeOrderPoolForm = () => {
    setShowOrderPoolForm(false);
    setSelectedOrderForPool(null);
    setOrderPoolForm(initialOrderPoolForm);
    setOrderPoolError("");
    setOrderPoolSubmitting(false);
  };

  const handleSubmitOrderPoolForm = async () => {
    if (!selectedOrderForPool?.id) return;
    const parsedPrice = orderPoolForm.dispatch_price === "" ? null : Number(orderPoolForm.dispatch_price);
    if (parsedPrice !== null && Number.isNaN(parsedPrice)) {
      setOrderPoolError("派单价必须是数字");
      alert("派单价必须是数字");
      return;
    }
    if (parsedPrice === null || parsedPrice <= 0) {
      setOrderPoolError("派单价必须大于0");
      alert("派单价必须大于0");
      return;
    }
    setOrderPoolError("");
    setOrderPoolSubmitting(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/admin/orders/${selectedOrderForPool.id}/order-pool`, {
        dispatch_price: parsedPrice,
      }, getAuthConfig());
      alert(`已放入订单池\n已通知司机数: ${response.data?.notified_drivers ?? 0}`);
      closeOrderPoolForm();
      refreshOrdersAndStats();
    } catch (error) {
      const message = getErrorMessage(error, "放入订单池失败");
      setOrderPoolError(message);
      alert(message);
      setOrderPoolSubmitting(false);
    }
  };

  const handleWithdrawOrderFromPool = async (orderId) => {
    if (!orderId) return;
    if (!window.confirm("确定要从订单池撤回这个订单吗？")) return;
    try {
      await axios.delete(`${apiBaseUrl}/admin/orders/${orderId}/order-pool`, getAuthConfig());
      alert("订单池订单已撤回");
      refreshOrdersAndStats();
    } catch (error) {
      alert(getErrorMessage(error, "撤回订单池失败"));
    }
  };

  const handleCreateDriverComplaint = async (order) => {
    const orderId = getDriverComplaintOrderId(order);
    if (!orderId) {
      showDriverComplaintNotice("无法打开投诉记录：订单缺少ID");
      return;
    }
    setSelectedOrderForComplaint({ ...order, id: orderId });
    setDriverComplaintForm(initialDriverComplaintForm);
    setDriverComplaintError("");
    setShowDriverComplaintForm(true);
    showDriverNotice("正在打开投诉记录");
    setDriverComplaintOptionsLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/driver-complaint-options`, getAuthConfig());
      setDriverComplaintOptions(response.data || []);
    } catch (error) {
      setDriverComplaintOptions([]);
    } finally {
      setDriverComplaintOptionsLoading(false);
    }
  };

  const closeDriverComplaintForm = () => {
    setShowDriverComplaintForm(false);
    setSelectedOrderForComplaint(null);
    setDriverComplaintForm(initialDriverComplaintForm);
    setDriverComplaintSubmitting(false);
    setDriverComplaintError("");
  };

  const handleDriverComplaintIssueToggle = (issueValue) => {
    setDriverComplaintForm((current) => {
      const issueTypes = current.issue_types.includes(issueValue)
        ? current.issue_types.filter((value) => value !== issueValue)
        : [...current.issue_types, issueValue];
      const defaultPoints = driverComplaintOptions
        .filter((option) => issueTypes.includes(option.value))
        .reduce((sum, option) => sum + Number(option.default_points || 0), 0);
      return {
        ...current,
        issue_types: issueTypes,
        deduction_points: defaultPoints ? String(defaultPoints) : current.deduction_points,
      };
    });
  };

  const handleSubmitDriverComplaintForm = async () => {
    const orderId = getDriverComplaintOrderId(selectedOrderForComplaint);
    if (!orderId) {
      setDriverComplaintError("无法提交投诉记录：订单缺少ID");
      showDriverComplaintNotice("无法提交投诉记录：订单缺少ID");
      return;
    }
    const points = Number(driverComplaintForm.deduction_points);
    if (!driverComplaintForm.issue_types.length) {
      setDriverComplaintError("请选择投诉事项");
      showDriverComplaintNotice("请选择投诉事项");
      return;
    }
    if (!points || Number.isNaN(points) || points <= 0) {
      setDriverComplaintError("扣分必须大于0");
      showDriverComplaintNotice("扣分必须大于0");
      return;
    }
    setDriverComplaintError("");
    setDriverComplaintSubmitting(true);
    try {
      await axios.post(`${apiBaseUrl}/admin/orders/${orderId}/driver-complaints`, {
        issue_types: driverComplaintForm.issue_types,
        deduction_points: points,
        note: driverComplaintForm.note,
      }, getAuthConfig());
      showDriverComplaintNotice("投诉记录已添加");
      closeDriverComplaintForm();
      refreshOrdersAndStats();
      fetchDriverAccounts?.();
    } catch (error) {
      const message = getErrorMessage(error, "添加投诉失败");
      setDriverComplaintError(message);
      showDriverComplaintNotice(message);
      setDriverComplaintSubmitting(false);
    }
  };

  const unresolvedDriverIssuesOnly = true;

  const openCreateDriverForm = () => {
    setEditingDriverAccount(null);
    setDriverAccountForm(initialDriverAccountForm);
    setShowDriverAccountForm(true);
  };

  const openEditDriverForm = (driver) => {
    setEditingDriverAccount(driver);
    setDriverAccountForm({
      name: driver.name,
      phone: driver.phone,
      password: "",
      can_view_revenue_stats: Boolean(driver.can_view_revenue_stats),
      include_in_internal_dispatch_overview: Boolean(driver.include_in_internal_dispatch_overview),
      can_request_leave: Boolean(driver.can_request_leave),
      instruction_region: driver.instruction_region || "other",
      instruction_header_id: driver.instruction_header_id || "",
      is_vip: Boolean(driver.is_vip),
    });
    setShowDriverAccountForm(true);
  };

  const closeDriverAccountForm = () => {
    setShowDriverAccountForm(false);
    setEditingDriverAccount(null);
  };

  const openDriverAssignModal = (order) => {
    const assignment = order.assigned_driver || {};
    const assignedFleet = order.assigned_fleet || {};
    const assignedDriverId = order.assigned_driver_id || assignment.driver_account_id || assignment.driver_id || "";
    setSelectedOrderForDriver(order);
    setDriverForm({
      ...initialDriverForm,
      ...assignment,
      vehicle_type: assignment.vehicle_type || order.vehicle_type || "alphard",
      fleet_id: order.assigned_fleet_id || assignedFleet.fleet_id || assignment.fleet_id || "",
      fleet_name: assignedFleet.fleet_name || assignment.fleet_name || "",
      driver_id: assignedDriverId,
      driver_name: assignment.driver_name || assignedFleet.fleet_name || "",
      driver_phone: assignment.driver_phone || assignedFleet.phone || "",
    });
    fetchDriverAccounts();
    setShowDriverAssignModal(true);
  };

  return {
    closeDriverAccountForm,
    closeDriverComplaintForm,
    closeDriverSchedule,
    closeOrderPoolForm,
    driverAccountForm,
    driverAccounts,
    driverAccountsError,
    driverAccountsLoading,
    driverExtraSchedules,
    driverComplaintForm,
    driverComplaintError,
    driverComplaintOptions,
    driverComplaintSubmitting,
    driverMonthlyStats,
    driverMonthlyStatsDispatcherId,
    driverMonthlyStatsDriverId,
    driverMonthlyStatsLoading,
    driverMonthlyStatsMonth,
    driverNoticeForm,
    driverNotices,
    driverOnboardingLoading,
    driverTrainingConfig,
    driverForm,
    driverScheduleLoading,
    driverScheduleOrders,
    editingDriverAccount,
    editingDriverNotice,
    driverNoticeTranslating,
    fetchDriverAccounts,
    fetchDriverNotices,
    fetchDriverMonthlyStats,
    fetchDriverOnboarding,
    fetchDriverSchedule,
    fetchSavedDrivers,
    handleAssignDriverToOrder,
    handleCancelExtraSchedule,
    handleCreateDriverAccount,
    handleDeleteDriver,
    handleDeleteDriverAccount,
    handleDriverMonthlyStatsDispatcherChange,
    handleDriverMonthlyStatsDriverChange,
    handleDriverMonthlyStatsMonthChange,
    handleSubmitDriverMonthlySettlements,
    handleUpdateDriverMonthlyDispatchPrice,
    handleExportDriverMonthlyStats,
    handleMigrateDriverOrders,
    handleNotifyAssignedDriver,
    handlePutOrderIntoPool,
    handleSubmitOrderPoolForm,
    handleWithdrawOrderFromPool,
    handleCreateDriverComplaint,
    handleDriverComplaintIssueToggle,
    handleSubmitDriverComplaintForm,
    handleResolveDriverIssue,
    handleReviewDriverOnboarding,
    handleEditDriverNotice,
    handleSaveDriverNotice,
    handleTranslateDriverNotice,
    handleSaveDriverTrainingConfig,
    handleResetDriverPassword,
    handleSaveDriver,
    handleToggleDriverActive,
    handleToggleDriverInternalDispatchOverview,
    handleToggleDriverPinned,
    handleToggleDriverRevenueStats,
    handleToggleFavoriteDriver,
    handleUnassignDriver,
    handleUpdateDriverAccount,
    hideCompletedDriverOrders,
    openCreateDriverForm,
    openDriverAssignModal,
    openEditDriverForm,
    savedDrivers,
    selectedDriverForSchedule,
    selectedDriverOnboarding,
    selectedOrderForComplaint,
    selectedOrderForPool,
    selectedOrderForDriver,
    setDriverAccountForm,
    setDriverComplaintForm,
    setDriverForm,
    setHideCompletedDriverOrders,
    setSelectedDriverOnboarding,
    setDriverNoticeForm,
    setDriverTrainingConfig,
    setShowDriverAssignModal,
    setShowDriverNoticeModal,
    setShowDriverTrainingConfig,
    showDriverAccountForm,
    showDriverComplaintForm,
    driverComplaintOptionsLoading,
    showDriverNoticeModal,
    showDriverTrainingConfig,
    showOrderPoolForm,
    orderPoolForm,
    orderPoolSubmitting,
    orderPoolError,
    setOrderPoolForm,
    fetchDriverTrainingConfig,
    unresolvedDriverIssuesOnly,
    showDriverAssignModal,
    showDriverSchedule,
  };
}
